// Onboarding voice tour widget. Fetches the tour manifest from the
// backend, streams each step's MP3 via <audio>, and steps through the
// script with next/prev/replay controls.
//
// Auto-opens on first visit (from `should_autoplay` on the manifest);
// after the last clip finishes the user can dismiss and it never
// auto-plays again (persisted on the User doc, not localStorage).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Play, Pause, SkipForward, SkipBack, X, Volume2, RotateCcw, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OnboardingVoiceTour = ({ token, forceOpen, onClose }) => {
  const [manifest, setManifest] = useState(null);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef(null);

  // Load manifest on mount
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/onboarding/tour`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (cancelled) return;
        setManifest(r.data);
        if (r.data.should_autoplay || forceOpen) {
          setOpen(true);
          setIdx(0);
        }
      })
      .catch(() => { /* silent — the tour is optional */ });
    return () => { cancelled = true; };
  }, [token, forceOpen]);

  // Load audio for current step. We use an authenticated axios GET
  // (rather than binding the URL directly on <audio src>) because the
  // MP3 endpoint requires a Bearer token — set the audio via a blob URL.
  const loadStepAudio = useCallback(async (stepIdx) => {
    if (!manifest?.steps?.[stepIdx]) return;
    const step = manifest.steps[stepIdx];
    setAudioLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/onboarding/tour/audio/${step.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      }
    } catch (e) { setError(e?.response?.data?.detail || 'Unable to load audio.'); }
    finally { setAudioLoading(false); }
  }, [manifest, token]);

  // On open + on step change, load audio
  useEffect(() => {
    if (open && manifest) loadStepAudio(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, manifest]);

  const total = manifest?.steps?.length || 0;
  const step = manifest?.steps?.[idx];
  const canPrev = idx > 0;
  const canNext = idx < total - 1;

  const handlePrev = () => { if (canPrev) setIdx((i) => i - 1); };
  const handleNext = () => { if (canNext) setIdx((i) => i + 1); };

  const finish = useCallback(async (action = 'closed') => {
    setPlaying(false);
    setOpen(false);
    if (audioRef.current) audioRef.current.pause();
    const currentStep = manifest?.steps?.[idx];
    try {
      // Fire-and-forget analytics event so the admin can see drop-off per step
      axios.post(`${API}/onboarding/tour/event`, {
        step_id: currentStep?.id, step_index: idx, action,
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      await axios.post(`${API}/onboarding/tour/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* non-fatal */ }
    if (onClose) onClose();
  }, [token, onClose, manifest, idx]);

  const handleEnded = () => {
    if (canNext) setIdx((i) => i + 1);
    else finish('completed');
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  if (!open || !step) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)] bg-slate-900 border border-teal-500/40 rounded-xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4"
      data-testid="onboarding-tour-widget"
    >
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between bg-gradient-to-r from-teal-600/30 to-teal-500/10 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <Volume2 className="w-4 h-4 text-teal-300 shrink-0" />
          <span className="text-teal-100 text-[11px] uppercase tracking-widest font-semibold shrink-0">Voice tour</span>
          <span className="text-white/40 text-xs shrink-0">· {idx + 1}/{total}</span>
        </div>
        <button
          onClick={() => finish('closed')}
          className="text-white/50 hover:text-white p-1 rounded"
          title="Close tour"
          data-testid="onboarding-tour-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4">
        <h3 className="text-white font-semibold text-sm mb-1" data-testid="onboarding-tour-title">{step.title}</h3>
        <p className="text-white/70 text-xs leading-snug" data-testid="onboarding-tour-body">{step.body}</p>
        {error && (
          <div className="mt-2 text-[11px] text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">
            {error}
          </div>
        )}
      </div>

      {/* Progress dots */}
      <div className="px-4 pb-2 flex items-center gap-1">
        {manifest.steps.map((s, i) => (
          <div
            key={s.id}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i < idx ? 'bg-teal-400' : i === idx ? 'bg-teal-300' : 'bg-white/10'
            }`}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="px-4 py-3 flex items-center justify-between border-t border-white/5 bg-black/20">
        <button
          onClick={handlePrev}
          disabled={!canPrev}
          className="text-white/70 hover:text-white disabled:opacity-30 p-1.5"
          data-testid="onboarding-tour-prev"
          title="Previous"
        >
          <SkipBack className="w-4 h-4" />
        </button>
        <button
          onClick={togglePlay}
          disabled={audioLoading}
          className="text-white bg-teal-500 hover:bg-teal-400 disabled:opacity-50 rounded-full w-9 h-9 flex items-center justify-center"
          data-testid="onboarding-tour-play"
          title={playing ? 'Pause' : 'Play'}
        >
          {audioLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <button
          onClick={handleNext}
          disabled={!canNext}
          className="text-white/70 hover:text-white disabled:opacity-30 p-1.5"
          data-testid="onboarding-tour-next"
          title="Next"
        >
          <SkipForward className="w-4 h-4" />
        </button>
      </div>

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onEnded={handleEnded}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
    </div>
  );
};

// Small "Replay tour" button — mount it in the header of My Performance
// so consultants can re-launch the tour whenever they want.
export const ReplayTourButton = ({ onReplay }) => (
  <button
    type="button"
    onClick={onReplay}
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-teal-500/40 bg-teal-500/10 hover:bg-teal-500/20 text-teal-200 text-xs font-semibold"
    data-testid="onboarding-tour-replay"
    title="Replay the voice tour"
  >
    <RotateCcw className="w-3 h-3" />Replay tour
  </button>
);

export default OnboardingVoiceTour;
